import { useMemo, useCallback, memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Icon } from '@cbhq/cds-web/icons';
import { deviceBreakpoints } from '@cbhq/cds-web/layout/breakpoints';
import { palette } from '@cbhq/cds-web/tokens';
import { computeLocaleCode, DEFAULT_LOCALE, getLoggedOutConfig, getSupportedLocalesFromConfig } from '@cbhq/intl';
import '../../utils/useIsMobile/index.js';
import '../GlobalNav/constants/index.js';
import { GLOBAL_EDITION_CODE } from '../GlobalPreferences/constants.js';
import { isGlobalEdition } from '../GlobalPreferences/utils/isGlobalEdition.js';
import { useRouter } from '../RouterContext.js';
import { jsx } from 'react/jsx-runtime';
import useIsMobile from '../../utils/useIsMobile/useIsMobile.js';
import { MOBILE_BREAKPOINT } from '../GlobalNav/constants/constants.js';
const i18nKey = 'LanguageSelectorCell';
const messages = defineMessages({
  selectedCheckMarkLabel: {
    id: `${i18nKey}.selectedCheckMarkLabel`,
    defaultMessage: 'Selected checkmark',
    description: `#Component: AriaLabel
    #CharLimit: 25
    #Context: Aria label for selected checkmark`
  }
});
function LanguageSelectorCell({
  language,
  countryConfig,
  localeInformation: {
    supportedLocales,
    selectedLocaleCode
  },
  userLocaleInfo
}) {
  const _useRouter = useRouter(),
    pathname = _useRouter.pathname;
  const _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  const isMobileNav = useIsMobile(MOBILE_BREAKPOINT);
  const isPhone = useIsMobile(deviceBreakpoints.tablet);
  const userCountryCode = useMemo(() => {
    const countryFromUrl = userLocaleInfo.countryFromUrl,
      countryFromIp = userLocaleInfo.countryFromIp,
      languageCode = userLocaleInfo.languageCode,
      localeFromUrl = userLocaleInfo.localeFromUrl;
    return isGlobalEdition({
      countryFromUrl,
      languageCode,
      countryFromIp,
      localeFromUrl
    }) ? GLOBAL_EDITION_CODE : countryFromUrl;
  }, [userLocaleInfo]);

  // Moved from the existing picker
  const handleChange = useCallback(() => {
    const langCode = language.tag;

    // If it is rest of the world, we use the language code as the locale code
    let localeCode = countryConfig.country_code === GLOBAL_EDITION_CODE ? langCode : computeLocaleCode({
      countryCode: countryConfig.country_code,
      countryDefaultLocale: countryConfig?.default_locale || DEFAULT_LOCALE,
      isDefaultLocaleSinglePath: !!countryConfig?.is_default_locale_single_path,
      languageCode: langCode
    });

    // for EN + US combination, the locale from URL will be "/"
    // Hence, if the locale code is not present, we use the default locale
    // which is "en" which will be redirected to "/"
    //
    // TODO: @gokul.kathirvel We can update the `computeLocaleCode` logic
    // to return the default locale code for EN + US combination
    localeCode = localeCode || DEFAULT_LOCALE;

    // globalNavLogEvent('logged_out_nav_language_selector_select', {
    //   action: ActionType.click,
    //   componentType: ComponentType.button,
    //   key: localeCode,
    // });

    const currentPath = pathname;
    let newPath = '';
    const config = getLoggedOutConfig();
    const supportedLocalesFromConfig = getSupportedLocalesFromConfig(config);
    const subfolderRegEx = new RegExp(
    // TODO: @gokul.kathirvel - once the supportedLocales is updated
    // we can remove `supportedLocalesFromConfig` from computation
    `^/(${[...supportedLocalesFromConfig, ...supportedLocales].join('|')})(/|$)`);
    if (currentPath === '/') {
      newPath = `/${localeCode}`; // Special case for the home page
    } else if (currentPath.match(subfolderRegEx)) {
      newPath = currentPath.replace(subfolderRegEx, `/${localeCode}/`);
    } else {
      newPath = `/${localeCode}${currentPath}`;
    }
    newPath = newPath.replace(/\/$/, ''); // Remove trailing slash, if exists

    // Forces a reload, forcing the client to go through the Node server's locale cookie setting process
    window.location.replace(newPath);
  }, [countryConfig, pathname, supportedLocales, language]);
  const isSelected = useMemo(() => {
    const isGlobal = userCountryCode === GLOBAL_EDITION_CODE;
    if (isGlobal && countryConfig.country_code === GLOBAL_EDITION_CODE) {
      return selectedLocaleCode === language.code || userLocaleInfo.localeFromUrl === language.tag.toLowerCase();
    }
    return userCountryCode === countryConfig.country_code && language.tag === userLocaleInfo.languageCode;
  }, [countryConfig, language, selectedLocaleCode, userCountryCode, userLocaleInfo]);
  const innerSpacing = useMemo(() => {
    //workaround to responsiveConfig not matching css breakpoints
    if (isPhone) {
      return {
        spacingHorizontal: 3
      };
    }
    if (!isMobileNav) {
      return {
        spacingHorizontal: 2
      };
    }
    return {
      spacingHorizontal: 4
    };
  }, [isMobileNav, isPhone]);
  return /*#__PURE__*/jsx(CellWrapper, {
    $isSelected: isSelected,
    children: /*#__PURE__*/jsx(SelectOption, {
      value: language.code,
      title: language.name,
      description: countryConfig.country_name,
      outerSpacing: {
        spacingVertical: 0,
        spacingHorizontal: 2
      },
      innerSpacing: innerSpacing,
      onPress: handleChange,
      borderRadius: "roundedNone",
      accessory: isSelected ? /*#__PURE__*/jsx(Icon, {
        color: "positive",
        name: "checkmark",
        size: "s",
        accessibilityLabel: formatMessage(messages.selectedCheckMarkLabel)
      }) : undefined
    }, language.code)
  });
}
const CellWrapper = styled.div`
  width: 100%;
  order: ${({
  $isSelected
}) => $isSelected ? -1 : 0};

  button {
    max-width: 100%;
    width: 100%;
    --background: ${({
  $isSelected
}) => $isSelected && palette.backgroundAlternate};
  }
`;
const LanguageSelectorCell$1 = /*#__PURE__*/memo(LanguageSelectorCell);
export { LanguageSelectorCell$1 as default };
//# sourceMappingURL=LanguageSelectorCell.js.map
